import { CLUB4X4_POLICY_DOCUMENTS_HREF } from '../claims/contactLinks'
import type { PolicyDocument } from '../components/PolicyDocumentsCard'

/** API returns document availability flags; dev mocks may still use legacy string values. */
export type PolicyDocumentLinks = Record<string, true | string>

function isPolicyDocumentAvailable(links: PolicyDocumentLinks, key: string): boolean {
  const value = links[key]
  return value === true || (typeof value === 'string' && value.length > 0)
}

/** Build downloadable policy documents from availability map (portal slug URLs only). */
export function buildDownloadablePolicyDocuments(
  policyId: string | undefined,
  documentLinks: PolicyDocumentLinks
): PolicyDocument[] {
  const documentsBasePath = policyId ? `/policies/${encodeURIComponent(policyId)}/documents` : ''

  return [
    {
      id: 'certificate-of-insurance',
      name: 'Certificate of Insurance',
      description:
        'An official summary of your policy used to confirm your coverage, limits and effective dates.',
      actions: ['download'],
      downloadUrl:
        isPolicyDocumentAvailable(documentLinks, 'certificate-of-insurance') && documentsBasePath
          ? `${documentsBasePath}/certificate-of-insurance`
          : undefined,
    },
    {
      id: 'certificate-of-currency',
      name: 'Certificate of Currency',
      description: 'Confirmation your cover is valid, active and paid.',
      actions: ['download'],
      downloadUrl:
        isPolicyDocumentAvailable(documentLinks, 'certificate-of-currency') && documentsBasePath
          ? `${documentsBasePath}/certificate-of-currency`
          : undefined,
    },
    {
      id: 'tax-invoice',
      name: 'Tax Invoice/Payment Receipt',
      description: 'Proof of payment, including vehicle details.',
      actions: ['download'],
      downloadUrl:
        isPolicyDocumentAvailable(documentLinks, 'tax-invoice') && documentsBasePath
          ? `${documentsBasePath}/tax-invoice`
          : undefined,
    },
  ]
}

/** External link to Club 4X4 policy documents (PDS, FSG, TMD). */
export const otherPolicyDocumentsEntry: PolicyDocument = {
  id: 'other-policy-documents',
  name: 'Other Policy Documents',
  description: 'Product Disclosure Statements, Financial Services Guides and Target Market Determinations.',
  actions: ['view'],
  externalUrl: CLUB4X4_POLICY_DOCUMENTS_HREF,
}

export function buildPolicyDocumentsPageList(
  policyId: string | undefined,
  documentLinks: PolicyDocumentLinks
): PolicyDocument[] {
  return [...buildDownloadablePolicyDocuments(policyId, documentLinks), otherPolicyDocumentsEntry]
}

type FetchDocumentFn = (endpoint: string) => Promise<Response>

/** Download a policy document via authenticated API fetch. */
export async function downloadPolicyDocument(
  doc: PolicyDocument,
  fetchDocument: FetchDocumentFn
): Promise<void> {
  if (!doc.downloadUrl) return

  const urlWithFormat = doc.downloadUrl.includes('?')
    ? `${doc.downloadUrl}&format=base64`
    : `${doc.downloadUrl}?format=base64`
  const res = await fetchDocument(urlWithFormat)
  if (!res.ok) throw new Error(res.statusText)
  const data = (await res.json()) as { contentType?: string; fileName?: string; base64: string }
  const binary = Uint8Array.from(atob(data.base64), (c) => c.charCodeAt(0))
  const blob = new Blob([binary], { type: data.contentType || 'application/pdf' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = doc.name.replace(/\s+/g, '-') + '.pdf'
  a.click()
  URL.revokeObjectURL(url)
}
